"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.TxBuilder = exports.ApplyOperations = exports.TxOperations = void 0;
exports.getDiffUpdate = getDiffUpdate;
exports.splitMixinUpdate = splitMixinUpdate;
exports.updateAttribute = updateAttribute;
exports.getAttributeUpdate = getAttributeUpdate;
const analytics_1 = require("@hcengineering/analytics");
const fast_equals_1 = require("fast-equals");
const _1 = require(".");
const component_1 = __importDefault(require("./component"));
const tx_1 = require("./tx");
/**
 * @public
 *
 * High Level operations with client, will create low level transactions.
 *
 * `notify` is not supported by TxOperations.
 */
class TxOperations {
    client;
    user;
    isDerived;
    meta;
    txFactory;
    constructor(client, user, isDerived = false, meta) {
        this.client = client;
        this.user = user;
        this.isDerived = isDerived;
        this.meta = meta;
        this.txFactory = new tx_1.TxFactory(user, isDerived);
    }
    getHierarchy() {
        return this.client.getHierarchy();
    }
    getModel() {
        return this.client.getModel();
    }
    async close() {
        await this.client.close();
    }
    findAll(_class, query, options) {
        return this.client.findAll(_class, query, options);
    }
    findOne(_class, query, options) {
        return this.client.findOne(_class, query, options);
    }
    domainRequest(domain, params) {
        return this.client.domainRequest(domain, params);
    }
    searchFulltext(query, options) {
        return this.client.searchFulltext(query, options);
    }
    tx(tx) {
        if (this.meta !== undefined) {
            tx.meta = { ...this.meta, ...(tx.meta ?? {}) };
        }
        return this.client.tx(tx);
    }
    async createDoc(_class, space, attributes, id, modifiedOn, modifiedBy, meta) {
        const hierarchy = this.client.getHierarchy();
        if (hierarchy.isDerived(_class, component_1.default.class.AttachedDoc)) {
            throw new Error('createDoc cannot be used for objects inherited from AttachedDoc');
        }
        if (hierarchy.findDomain(_class) === _1.DOMAIN_MODEL && space !== component_1.default.space.Model) {
            throw new Error('createDoc cannot be called for DOMAIN_MODEL classes with non-model space');
        }
        const tx = this.txFactory.createTxCreateDoc(_class, space, attributes, id, modifiedOn, modifiedBy, meta);
        await this.tx(tx);
        return tx.objectId;
    }
    async addCollection(_class, space, attachedTo, attachedToClass, collection, attributes, id, modifiedOn, modifiedBy, meta) {
        const tx = this.txFactory.createTxCollectionCUD(attachedToClass, attachedTo, space, collection, this.txFactory.createTxCreateDoc(_class, space, attributes, id, modifiedOn, modifiedBy, meta), modifiedOn, modifiedBy, meta);
        await this.tx(tx);
        return tx.objectId;
    }
    async updateCollection(_class, space, objectId, attachedTo, attachedToClass, collection, operations, retrieve, modifiedOn, modifiedBy, meta) {
        const tx = this.txFactory.createTxCollectionCUD(attachedToClass, attachedTo, space, collection, this.txFactory.createTxUpdateDoc(_class, space, objectId, operations, retrieve, modifiedOn, modifiedBy, meta), modifiedOn, modifiedBy, meta);
        await this.tx(tx);
        return attachedTo;
    }
    async removeCollection(_class, space, objectId, attachedTo, attachedToClass, collection, modifiedOn, modifiedBy, meta) {
        const tx = this.txFactory.createTxCollectionCUD(attachedToClass, attachedTo, space, collection, this.txFactory.createTxRemoveDoc(_class, space, objectId, modifiedOn, modifiedBy, meta), modifiedOn, modifiedBy, meta);
        await this.tx(tx);
        return attachedTo;
    }
    updateDoc(_class, space, objectId, operations, retrieve, modifiedOn, modifiedBy, meta) {
        const tx = this.txFactory.createTxUpdateDoc(_class, space, objectId, operations, retrieve, modifiedOn, modifiedBy, meta);
        return this.tx(tx);
    }
    removeDoc(_class, space, objectId, modifiedOn, modifiedBy, meta) {
        const tx = this.txFactory.createTxRemoveDoc(_class, space, objectId, modifiedOn, modifiedBy, meta);
        return this.tx(tx);
    }
    createMixin(objectId, objectClass, objectSpace, mixin, attributes, modifiedOn, modifiedBy, meta) {
        const tx = this.txFactory.createTxMixin(objectId, objectClass, objectSpace, mixin, attributes, modifiedOn, modifiedBy, meta);
        return this.tx(tx);
    }
    updateMixin(objectId, objectClass, objectSpace, mixin, attributes, modifiedOn, modifiedBy, meta) {
        const tx = this.txFactory.createTxMixin(objectId, objectClass, objectSpace, mixin, attributes, modifiedOn, modifiedBy, meta);
        return this.tx(tx);
    }
    async update(doc, update, retrieve, modifiedOn, modifiedBy, meta) {
        const hierarchy = this.client.getHierarchy();
        const mixClass = _1.Hierarchy.mixinOrClass(doc);
        if (hierarchy.isMixin(mixClass)) {
            const baseClass = hierarchy.getBaseClass(doc._class);
            const byClass = splitMixinUpdate(hierarchy, update, mixClass, baseClass);
            const ops = this.apply(doc._id);
            for (const it of byClass) {
                if (hierarchy.isMixin(it[0])) {
                    await ops.updateMixin(doc._id, baseClass, doc.space, it[0], it[1], modifiedOn, modifiedBy, meta);
                }
                else {
                    if (hierarchy.isDerived(it[0], component_1.default.class.AttachedDoc)) {
                        const adoc = doc;
                        return await this.updateCollection(it[0], doc.space, adoc._id, adoc.attachedTo, adoc.attachedToClass, adoc.collection, it[1], retrieve, modifiedOn, modifiedBy, meta);
                    }
                    await ops.updateDoc(it[0], doc.space, doc._id, it[1], retrieve, modifiedOn, modifiedBy, meta);
                }
            }
            return await ops.commit();
        }
        if (hierarchy.isDerived(doc._class, component_1.default.class.AttachedDoc)) {
            const adoc = doc;
            return await this.updateCollection(doc._class, doc.space, adoc._id, adoc.attachedTo, adoc.attachedToClass, adoc.collection, update, retrieve, modifiedOn, modifiedBy, meta);
        }
        return await this.updateDoc(doc._class, doc.space, doc._id, update, retrieve, modifiedOn, modifiedBy, meta);
    }
    remove(doc, modifiedOn, modifiedBy, meta) {
        if (this.client.getHierarchy().isDerived(doc._class, component_1.default.class.AttachedDoc)) {
            const adoc = doc;
            return this.removeCollection(doc._class, doc.space, adoc._id, adoc.attachedTo, adoc.attachedToClass, adoc.collection, modifiedOn, modifiedBy, meta);
        }
        return this.removeDoc(doc._class, doc.space, doc._id, modifiedOn, modifiedBy, meta);
    }
    apply(scope, measure, derived) {
        return new ApplyOperations(this, scope, measure, derived ?? this.isDerived);
    }
    async diffUpdate(doc, update, date, account, meta) {
        const documentUpdate = getDiffUpdate(doc, update);
        if (Object.keys(documentUpdate).length > 0) {
            await this.update(doc, documentUpdate, false, date ?? Date.now(), account, meta);
            tx_1.TxProcessor.applyUpdate(doc, documentUpdate);
        }
        return doc;
    }
    async mixinDiffUpdate(doc, raw, mixin, modifiedBy, modifiedOn) {
        // We need to update fields if they are different.
        if (!this.getHierarchy().hasMixin(doc, mixin)) {
            await this.createMixin(doc._id, doc._class, doc.space, mixin, raw, modifiedOn, modifiedBy);
            tx_1.TxProcessor.applyUpdate(this.getHierarchy().as(doc, mixin), raw);
            return doc;
        }
        const documentUpdate = {};
        for (const [k, v] of Object.entries(raw)) {
            if (['_class', '_id', 'modifiedBy', 'modifiedOn', 'space', 'attachedTo', 'attachedToClass'].includes(k)) {
                continue;
            }
            const dv = doc[k];
            if (!(0, fast_equals_1.deepEqual)(dv, v) && v != null) {
                ;
                documentUpdate[k] = v;
            }
        }
        if (Object.keys(documentUpdate).length > 0) {
            await this.updateMixin(doc._id, doc._class, doc.space, mixin, documentUpdate, modifiedOn, modifiedBy);
            tx_1.TxProcessor.applyUpdate(this.getHierarchy().as(doc, mixin), documentUpdate);
        }
        return doc;
    }
}
exports.TxOperations = TxOperations;
function getDiffUpdate(doc, update) {
    // We need to update fields if they are different.
    const documentUpdate = {};
    for (const [k, v] of Object.entries(update)) {
        if (['_class', '_id', 'modifiedBy', 'modifiedOn', 'space', 'attachedTo', 'attachedToClass'].includes(k)) {
            continue;
        }
        const dv = doc[k];
        if (!(0, fast_equals_1.deepEqual)(dv, v) && v !== undefined) {
            ;
            documentUpdate[k] = v;
        }
    }
    return documentUpdate;
}
function splitMixinUpdate(hierarchy, update, mixClass, baseClass) {
    const attributes = hierarchy.getAllAttributes(mixClass);
    const updateAttrs = Object.fromEntries(Object.entries(update).filter((it) => !it[0].startsWith('$')));
    const updateOps = Object.fromEntries(Object.entries(update).filter((it) => it[0].startsWith('$')));
    const result = splitObjectAttributes(hierarchy, updateAttrs, baseClass, attributes);
    for (const [key, value] of Object.entries(updateOps)) {
        const updates = splitObjectAttributes(hierarchy, value, baseClass, attributes);
        for (const [opsClass, opsUpdate] of updates) {
            const upd = result.get(opsClass) ?? {};
            result.set(opsClass, { ...upd, [key]: opsUpdate });
        }
    }
    return result;
}
function splitObjectAttributes(hierarchy, obj, objClass, attributes) {
    const result = new Map();
    for (const [key, value] of Object.entries(obj)) {
        const attributeOf = attributes.get(key)?.attributeOf;
        const clazz = attributeOf !== undefined && hierarchy.isMixin(attributeOf) ? attributeOf : objClass;
        result.set(clazz, { ...(result.get(clazz) ?? {}), [key]: value });
    }
    return result;
}
/**
 * @public
 *
 * Builder for ApplyOperation, with same syntax as TxOperations.
 *
 * Will send real command on commit and will return boolean of operation success.
 */
class ApplyOperations extends TxOperations {
    ops;
    scope;
    measureName;
    txes = [];
    matches = [];
    notMatches = [];
    constructor(ops, scope, measureName, isDerived) {
        const txClient = {
            getHierarchy: () => ops.client.getHierarchy(),
            getModel: () => ops.client.getModel(),
            close: () => ops.client.close(),
            findOne: (_class, query, options) => ops.client.findOne(_class, query, options),
            findAll: (_class, query, options) => ops.client.findAll(_class, query, options),
            searchFulltext: (query, options) => ops.client.searchFulltext(query, options),
            domainRequest: (domain, params) => ops.client.domainRequest(domain, params),
            tx: async (tx) => {
                if (tx_1.TxProcessor.isExtendsCUD(tx._class)) {
                    this.txes.push(tx);
                }
                return {};
            }
        };
        super(txClient, ops.user, isDerived ?? false);
        this.ops = ops;
        this.scope = scope;
        this.measureName = measureName;
    }
    match(_class, query) {
        this.matches.push({ _class, query });
        return this;
    }
    notMatch(_class, query) {
        this.notMatches.push({ _class, query });
        return this;
    }
    async commit(notify = true, extraNotify = []) {
        if (this.txes.length === 1 &&
            this.matches.length === 0 &&
            this.notMatches.length === 0 &&
            this.measureName == null) {
            const st = (0, _1.platformNow)();
            // Individual update, no need for apply
            await this.ops.tx(this.txes[0]);
            const time = (0, _1.platformNow)() - st;
            this.txes = [];
            return {
                result: true,
                time,
                serverTime: time
            };
        }
        if (this.txes.length > 0) {
            const st = (0, _1.platformNow)();
            const aop = this.ops.txFactory.createTxApplyIf(component_1.default.space.Tx, this.scope, this.matches, this.notMatches, this.txes, this.measureName, notify, extraNotify);
            const result = (await this.ops.tx(aop));
            const dnow = (0, _1.platformNow)();
            if (typeof window === 'object' && window !== null && this.measureName != null) {
                console.log(`measure ${this.measureName}`, dnow - st, 'server time', result.serverTime);
            }
            if (!result.success) {
                console.warn('ops.commit() failed', {
                    scope: this.scope,
                    measureName: this.measureName,
                    matchCount: this.matches.length,
                    notMatchCount: this.notMatches.length,
                    txCount: this.txes.length,
                    matches: this.matches.map((m) => ({ _class: m._class, query: m.query })),
                    notMatches: this.notMatches.map((m) => ({ _class: m._class, query: m.query }))
                });
            }
            this.txes = [];
            return {
                result: result.success,
                time: dnow - st,
                serverTime: result.serverTime
            };
        }
        return { result: true, time: 0, serverTime: 0 };
    }
    // Apply for this will reuse, same apply context.
    apply(scope, measure) {
        return this;
    }
}
exports.ApplyOperations = ApplyOperations;
/**
 * @public
 *
 * Builder for TxOperations.
 */
class TxBuilder extends TxOperations {
    hierarchy;
    modelDb;
    txes = [];
    matches = [];
    constructor(hierarchy, modelDb, user) {
        const txClient = {
            getHierarchy: () => this.hierarchy,
            getModel: () => this.modelDb,
            close: async () => { },
            findOne: async (_class, query, options) => undefined,
            findAll: async (_class, query, options) => (0, _1.toFindResult)([]),
            searchFulltext: async (query, options) => ({ docs: [] }),
            domainRequest: async (domain, params) => ({ domain, value: null }),
            tx: async (tx) => {
                if (tx_1.TxProcessor.isExtendsCUD(tx._class)) {
                    this.txes.push(tx);
                }
                return {};
            }
        };
        super(txClient, user);
        this.hierarchy = hierarchy;
        this.modelDb = modelDb;
    }
}
exports.TxBuilder = TxBuilder;
/**
 * @public
 */
async function updateAttribute(client, object, _class, attribute, value, saveModified = false, analyticsProps = {}) {
    const doc = object;
    const attributeKey = attribute.key;
    if (doc[attributeKey] === value)
        return;
    const modifiedOn = saveModified ? doc.modifiedOn : Date.now();
    const modifiedBy = attribute.key === 'modifiedBy' ? value : saveModified ? doc.modifiedBy : undefined;
    const attr = attribute.attr;
    const baseAnalyticsProps = {
        objectClass: _class,
        objectId: object._id,
        attribute: attributeKey,
        ...analyticsProps
    };
    if (client.getHierarchy().isMixin(attr.attributeOf)) {
        await client.updateMixin(doc._id, _class, doc.space, attr.attributeOf, { [attributeKey]: value }, modifiedOn, modifiedBy);
        analytics_1.Analytics.handleEvent('ChangeAttribute', { ...baseAnalyticsProps, value });
    }
    else {
        if (client.getHierarchy().isDerived(attribute.attr.type._class, component_1.default.class.ArrOf)) {
            const oldValue = object[attributeKey] ?? [];
            const val = Array.isArray(value) ? value : [value];
            const toPull = oldValue.filter((it) => !val.includes(it));
            const toPush = val.filter((it) => !oldValue.includes(it));
            if (toPull.length > 0) {
                await client.update(object, { $pull: { [attributeKey]: { $in: toPull } } }, false, modifiedOn, modifiedBy);
                analytics_1.Analytics.handleEvent('RemoveCollectionItems', {
                    ...baseAnalyticsProps,
                    removed: toPull
                });
            }
            if (toPush.length > 0) {
                await client.update(object, { $push: { [attributeKey]: { $each: toPush, $position: 0 } } }, false, modifiedOn, modifiedBy);
                analytics_1.Analytics.handleEvent('AddCollectionItems', {
                    ...baseAnalyticsProps,
                    added: toPush
                });
            }
        }
        else {
            await client.update(object, { [attributeKey]: value }, false, modifiedOn, modifiedBy);
            analytics_1.Analytics.handleEvent('SetCollectionItems', {
                ...baseAnalyticsProps,
                value
            });
        }
    }
}
function getAttributeUpdate(client, object, _class, attribute, value) {
    const doc = object;
    const attributeKey = attribute.key;
    if (doc[attributeKey] === value)
        return {};
    const attr = attribute.attr;
    if (client.getHierarchy().isMixin(attr.attributeOf)) {
        return { [`${attr.attributeOf}.${attributeKey}`]: value };
    }
    else {
        const update = {};
        if (client.getHierarchy().isDerived(attribute.attr.type._class, component_1.default.class.ArrOf)) {
            const oldValue = object[attributeKey] ?? [];
            const val = Array.isArray(value) ? value : [value];
            const toPull = oldValue.filter((it) => !val.includes(it));
            const toPush = val.filter((it) => !oldValue.includes(it));
            if (toPull.length > 0) {
                update.$pull = { [attributeKey]: { $in: toPull } };
                if (toPush.length > 0) {
                    update.$push = { [attributeKey]: { $each: toPush, $position: 0 } };
                }
            }
            else {
                return { [attributeKey]: value };
            }
        }
    }
    return {};
}
//# sourceMappingURL=operations.js.map